import http from '@monolith/legacy/services/api/http';
import { captureBrandsException } from '@core/plugins/sentry/helper';
import type { AxiosResponse } from 'axios';
import { getOffsetFromPage } from './pagination';
import { ITEMS_PER_PAGE } from '../constants';
import type { MediaResponse, MediaParams } from '../types';
import type { UploadMediaData, GetVideosParams, GetVideosData } from '@ankorstore/api-internal/data-contracts';


const jsonApiConfigs = {
  headers: {
    'Content-Type': 'application/vnd.api+json',
  },
};

type GetMediaParams = {
  page?: number,
  itemsPerPage?: number,
  query?: string
};

export const getMedia = async (params?: GetMediaParams): Promise<MediaResponse> => {

  const { page = 1, itemsPerPage = ITEMS_PER_PAGE, query } = params || {};

  try {
    const { data }: AxiosResponse<MediaResponse> = await http().get(`/api/internal/v1/media`, {
      ...jsonApiConfigs,
      params: {
        'filter[query]': query,
        'page[limit]': itemsPerPage,
        'page[offset]': getOffsetFromPage(page, itemsPerPage)
      } as MediaParams
    });
    return data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'media-library/api' },
      { label: 'action', value: 'getMedia' },
    ]);
    return null;
  }
};

export const uploadMedia = async (files: File[]): Promise<UploadMediaData> => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files[]', file);
  });

  try {
    const { data }: AxiosResponse<UploadMediaData> = await http().post('/api/internal/v1/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'media-library/api' },
      { label: 'action', value: 'uploadMedia' },
    ]);
    return null;
  }
};

export const getVideos = async (params: GetVideosParams): Promise<GetVideosData | null> => {
  try {
    const queryParams = new URLSearchParams();

    if (params['filter[brandUuid]']) {
      queryParams.set('filter[brandUuid]', params['filter[brandUuid]']);
    }
    if (params['filter[displayContext]']) {
      queryParams.set('filter[displayContext]', params['filter[displayContext]']);
    }
    if (params['filter[query]']) {
      queryParams.set('filter[query]', params['filter[query]']);
    }
    if (params['page[limit]'] !== undefined) {
      queryParams.set('page[limit]', String(params['page[limit]']));
    }
    if (params['page[offset]'] !== undefined) {
      queryParams.set('page[offset]', String(params['page[offset]']));
    }

    const url = `/api/internal/v1/videos?${queryParams.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      return null;
    }

    const data: GetVideosData = await response.json();
    return data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'media-library/api' },
      { label: 'action', value: 'getVideos' },
    ]);
    return null;
  }
};
